// ⚠️  TEAMS LINK RENDERING — READ _docs/TEAMS_LINK_HANDLING.md BEFORE EDITING ⚠️
//
// In Teams Desktop, React synthetic onClick silently fails for buttons inside scrollable
// containers (WebView2 sandboxed iframe event-delegation quirk).  Teams link buttons
// therefore carry data-external-href (not onClick) so the global pointerdown capture
// listener in teamsLinkCaptureBoot.ts handles the click via native DOM events.
//
// DO NOT add onClick to the Teams button branch.
// DO NOT add data-open-in-app="true" to the Teams button branch (that tells the
// capture listener to skip the element, which breaks link opening entirely).
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isTeamsEmbedded } from '../utils/teamsHost';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  theme?: 'dark' | 'light';
  /** Called instead of openExternalLink when a .pdf link is clicked (e.g. to open the in-app viewer). */
  onPdfLink?: (href: string) => void;
}

const linkClassName =
  'text-cyan-400 hover:text-cyan-300 underline hover:no-underline transition-colors cursor-pointer relative z-10';

// select-text overrides the WebKit/Chromium browser default of user-select:none on <button>
// elements, so that users can select and copy link text inside Teams Desktop.
const teamsLinkButtonClassName =
  `${linkClassName} bg-transparent border-0 p-0 text-left text-sm font-inherit inline cursor-pointer select-text`;

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = "", theme = 'dark', onPdfLink }) => {
  const isDark = theme === 'dark';
  const inTeams = isTeamsEmbedded();

  return (
    <ReactMarkdown
      className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : 'prose-slate'} ${className}`}
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node: _node, ...props }) => <h1 className={`text-lg font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        h2: ({ node: _node, ...props }) => <h2 className={`text-base font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        h3: ({ node: _node, ...props }) => <h3 className={`text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        h4: ({ node: _node, ...props }) => <h4 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        h5: ({ node: _node, ...props }) => <h5 className={`text-xs font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        h6: ({ node: _node, ...props }) => <h6 className={`text-xs font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        strong: ({ node: _node, ...props }) => <strong className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />,
        em: ({ node: _node, ...props }) => <em className={`italic ${isDark ? 'text-gray-200' : 'text-gray-700'}`} {...props} />,
        code: ({ node: _node, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || '');
          return match ? (
            <pre className={`rounded-md p-4 overflow-x-auto border my-4 ${isDark ? 'bg-black border-gray-700' : 'bg-gray-50 border-gray-300'}`}>
              <code className={`language-${match[1]} text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`} {...props}>
                {children}
              </code>
            </pre>
          ) : (
            <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${isDark ? 'bg-black text-cyan-300' : 'bg-gray-100 text-cyan-700'}`} {...props}>
              {children}
            </code>
          );
        },
        blockquote: ({ node: _node, ...props }) => (
          <blockquote className={`border-l-4 border-cyan-500 pl-4 italic text-sm my-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} {...props} />
        ),
        ul: ({ node: _node, ...props }) => <ul className={`list-disc list-inside mb-3 text-sm space-y-1 ${isDark ? 'text-gray-200' : 'text-gray-900'}`} {...props} />,
        ol: ({ node: _node, ...props }) => <ol className={`list-decimal list-outside pl-6 mb-3 text-sm space-y-1 ${isDark ? 'text-gray-200' : 'text-gray-900'}`} {...props} />,
        li: ({ node: _node, ...props }) => <li className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'} leading-relaxed`} {...props} />,
        a: ({ node: _node, href, children }) => {
          if (!href) {
            return <span>{children}</span>;
          }
          const isPdf = href.toLowerCase().endsWith('.pdf');
          if (inTeams) {
            // Inside Teams: rely on the global capture listener (teamsLinkCaptureBoot) which
            // intercepts pointerdown on button[data-external-href] elements.  React's synthetic
            // onClick can silently fail to fire in Teams Desktop (WebView2 event-delegation quirk),
            // so we deliberately omit it and let the native-phase capture listener own the click.
            return (
              <button
                type="button"
                className={teamsLinkButtonClassName}
                data-external-href={href}
                data-open-external={href}
              >
                {children}
              </button>
            );
          }
          // Outside Teams: PDF links open the in-app viewer; everything else opens in a new tab.
          if (isPdf && onPdfLink) {
            return (
              <button
                type="button"
                className={teamsLinkButtonClassName}
                onClick={() => onPdfLink(href)}
              >
                {children}
              </button>
            );
          }
          return (
            <a href={href} className={linkClassName} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        },
        table: ({ node: _node, ...props }) => (
          <div className="overflow-x-auto my-4">
            <table className={`min-w-full border rounded-lg ${isDark ? 'border-gray-700' : 'border-gray-300'}`} {...props} />
          </div>
        ),
        thead: ({ node: _node, ...props }) => <thead className={isDark ? 'bg-gray-800' : 'bg-gray-100'} {...props} />,
        tbody: ({ node: _node, ...props }) => <tbody className={isDark ? 'bg-gray-900' : 'bg-white'} {...props} />,
        tr: ({ node: _node, ...props }) => <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`} {...props} />,
        th: ({ node: _node, ...props }) => (
          <th className={`px-3 py-2 text-left font-semibold text-sm border-r last:border-r-0 ${isDark ? 'text-white border-gray-700' : 'text-gray-900 border-gray-300'}`} {...props} />
        ),
        td: ({ node: _node, ...props }) => (
          <td className={`px-3 py-2 text-sm border-r last:border-r-0 ${isDark ? 'text-gray-200 border-gray-700' : 'text-gray-900 border-gray-300'}`} {...props} />
        ),
        hr: ({ node: _node, ...props }) => <hr className={`my-6 ${isDark ? 'border-gray-600' : 'border-gray-300'}`} {...props} />,
        p: ({ node: _node, ...props }) => <p className={`mb-3 text-sm leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-900'}`} {...props} />,
        input: ({ node: _node, ...props }) => (
          <input type="checkbox" className="mr-2 accent-cyan-500" disabled {...props} />
        ),
        del: ({ node: _node, ...props }) => <del className="line-through text-gray-400" {...props} />,
        img: ({ node: _node, ...props }) => (
          <img className="max-w-full h-auto rounded-lg border border-gray-700 my-4" {...props} />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
